import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';

export interface BodyMeasurement {
  id: string;
  user_id: string;
  measured_at: string;
  neck_cm: number | null;
  waist_cm: number | null;
  hips_cm: number | null;
  glutes_cm: number | null;
  body_fat_percent: number | null;
  notes: string | null;
  created_at: string;
}

export function useMeasurements() {
  const { profile } = useUserStore();
  const [measurements, setMeasurements] = useState<BodyMeasurement[]>([]);
  const [loading, setLoading] = useState(false);
  async function load() {
    if (!profile?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('body_measurements')
      .select('*')
      .eq('user_id', profile.id)
      .order('measured_at', { ascending: false })
      .limit(50);
    if (data) setMeasurements(data as BodyMeasurement[]);
    setLoading(false);
  }
  useEffect(() => {
    load();
  }, [profile?.id]);
  return { measurements, loading, refetch: load };
}

export async function addMeasurement(userId: string, m: {
  neck_cm?: number;
  waist_cm?: number;
  hips_cm?: number;
  glutes_cm?: number;
  body_fat_percent?: number;
  notes?: string;
}) {
  const { data, error } = await supabase
    .from('body_measurements')
    .insert({ user_id: userId, ...m })
    .select()
    .single();
  if (error) throw error;
  
  // 🆕 Геймификация — асинхронно
  awardMeasurementGamification(userId).catch(err => {
    console.error('[addMeasurement] gamification error:', err);
  });
  
  return data as BodyMeasurement;
}

// 🆕 Геймификация после замеров
async function awardMeasurementGamification(userId: string) {
  try {
    // 1. +15 XP за замеры
    await supabase.rpc('add_xp', { uid: userId, amount: 15 });
    
    // 2. Считаем общее количество замеров
    const { count } = await supabase
      .from('body_measurements')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    const total = count || 0;
    
    // 3. Проверяем first_measurement
    await supabase.rpc('check_achievement', { 
      uid: userId, 
      ach_id: 'first_measurement', 
      new_progress: total 
    });
    
    // 4. measurements_4 — 4 раза замерил
    if (total >= 4) {
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'measurements_4', 
        new_progress: total 
      });
    }
  } catch (e) {
    console.error('[awardMeasurementGamification] error:', e);
  }
}

export function daysFromLast(measurements: BodyMeasurement[]): number | null {
  if (!measurements.length) return null;
  const latest = new Date(measurements[0].measured_at);
  const days = Math.floor((Date.now() - latest.getTime()) / (1000 * 60 * 60 * 24));
  return days;
}
